
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocation, useNavigate } from "react-router-dom";
import { SignInForm } from "@/features/auth/components/SignInForm";
import { SignUpForm } from "@/features/auth/components/SignUpForm";
import { ForgotPasswordForm } from "@/features/auth/components/ForgotPasswordForm";
import { Check, ChevronLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SidebarProvider } from "@/components/ui/sidebar";
import { MetaTags } from "@/components/seo/MetaTags";

const Auth = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'signup' ? 'signup' : (tabParam === 'forgot-password' ? 'forgot-password' : 'signin'));

  useEffect(() => {
    if (user) {
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/welcome';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  return (
    <SidebarProvider>
      <MetaTags 
        title="Authentification"
        description="Connectez-vous ou créez un compte sur AlunaTools"
        noIndex={true}
      />
      <div className="min-h-screen flex bg-gray-50 w-full">
        <div className="w-full flex flex-col md:flex-row">
          <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-0">
            <div className="w-full max-w-md">
              <Card className="w-full border-none shadow-none bg-transparent">
                <CardContent className="p-0">
                  {activeTab === 'forgot-password' ? (
                    <div>
                      <button 
                        onClick={() => setActiveTab('signin')} 
                        className="text-blue-600 hover:text-blue-800 mb-6 flex items-center"
                      >
                        <ChevronLeft className="mr-1 h-4 w-4" />
                        Retour à la connexion
                      </button>
                      <ForgotPasswordForm 
                        isSubmitting={isSubmitting} 
                        setIsSubmitting={setIsSubmitting} 
                      />
                    </div>
                  ) : (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                      <TabsList className="grid w-full grid-cols-2 mb-6">
                        <TabsTrigger value="signin">Connexion</TabsTrigger>
                        <TabsTrigger value="signup">Inscription</TabsTrigger>
                      </TabsList>

                      <TabsContent value="signin">
                        <SignInForm 
                          isSubmitting={isSubmitting} 
                          setIsSubmitting={setIsSubmitting}
                          onForgotPasswordClick={() => setActiveTab('forgot-password')}
                        />
                      </TabsContent>

                      <TabsContent value="signup">
                        <SignUpForm 
                          isSubmitting={isSubmitting} 
                          setIsSubmitting={setIsSubmitting} 
                        />
                      </TabsContent>
                    </Tabs>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="hidden md:flex md:w-1/2 bg-[#f4f7fd] items-center justify-center">
            <div className="p-12 max-w-md">
              <div className="mb-10">
                <img 
                  src="/images/alunatools/e2b3cf9e-19de-4b54-9306-7b53664ced03.png" 
                  alt="AlunaTools Logo" 
                  className="h-16 w-auto"
                />
              </div>
              
              <div className="text-gray-800">
                <h2 className="text-3xl font-bold mb-8">Accédez <span className="font-extrabold">gratuitement</span> à tous les outils d'AlunaTools</h2>
                
                <div className="space-y-5 mb-12">
                  <div className="flex items-center">
                    <div className="mr-3 bg-[#EFB036] p-1 rounded-full">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-lg font-medium">Inscription rapide</span>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="mr-3 bg-[#EFB036] p-1 rounded-full">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-lg font-medium">Aucun engagement</span>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="mr-3 bg-[#EFB036] p-1 rounded-full">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-lg font-medium">Des outils concrets pour booster votre business</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-gray-600 text-sm font-medium">
          AlunaTools — Propulsez votre activité 🚀
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Auth;
